import { arrayMove } from '@dnd-kit/sortable';
import {
  FilterSteps,
  FilterValues,
  onDeleteStepProps,
  onReorderStepsProps
} from './MatchDrawerTables.types';
import {
  DEFAULT_EMPLOYEE_FLEX_FILTER,
  FLEX_DRAWER_FILTER_DEFAULT_VALUES
} from './MatchDrawerTables.consts';

export const gotoPrevStep = ({
  setCurrentStepIndex,
  applyStepFilters,
  filterSteps
}) => {
  setCurrentStepIndex((index) => {
    const start = index < 0 ? 0 : index;
    const next = Math.max(start - 1, 0);
    applyStepFilters(filterSteps[next]);
    return next;
  });
};

export const gotoNextStep = ({
  setCurrentStepIndex,
  applyStepFilters,
  filterSteps
}) => {
  setCurrentStepIndex((index) => {
    const start = index < 0 ? -1 : index;
    const next = Math.min(start + 1, filterSteps.length - 1);
    applyStepFilters(filterSteps[next]);
    return next;
  });
};

export const onReorderSteps = ({
  activeId,
  overId,
  setFilterSteps,
  currentStepIndex,
  setCurrentStepIndex,
  persistSteps
}: onReorderStepsProps) => {
  setFilterSteps((prev) => {
    const oldIndex = prev.findIndex((step) => step.id === activeId);
    const newIndex = prev.findIndex((step) => step.id === overId);
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return prev;

    const next = arrayMove(prev, oldIndex, newIndex).map((step, index) => ({
      ...step,
      index
    }));

    // keep the selection pinned to the same step by id
    const selectedId = prev[currentStepIndex]?.id;
    if (selectedId != null) {
      const newSelectedIndex = next.findIndex(
        (selected) => selected.id === selectedId
      );
      setCurrentStepIndex(newSelectedIndex);
    }

    persistSteps(next);
    return next;
  });
};

export const toFormFromStep = (step?: FilterSteps) => {
  const base = { ...FLEX_DRAWER_FILTER_DEFAULT_VALUES } as Record<string, any>;
  if (!step) return base;

  for (const [key, value] of Object.entries(step.checkedFilters ?? {})) {
    if (key === 'balanceHours' && !!value && value !== '') {
      const balance = Number(value);
      if (Number.isFinite(balance)) base[key] = balance;
    } else {
      base[key] = value === true || value === 'true';
    }
  }
  base.stepName = step.name ?? '';
  return base;
};

export const onDeleteStep = ({
  id,
  setFilters,
  setFilterSteps,
  currentStepIndex,
  setCurrentStepIndex,
  methods,
  applyStepFilters,
  persistSteps
}: onDeleteStepProps) => {
  setFilterSteps((prev) => {
    const idx = prev.findIndex((step) => step.id === id);
    if (idx < 0) return prev;

    const next = prev
      .filter((step) => step.id !== id)
      .map((step, index) => ({ ...step, index })); // normalize indices

    // selection logic
    if (idx === currentStepIndex) {
      // Deleted the currently selected step
      if (next.length === 0) {
        // nothing left, clear selection + reset filters
        setCurrentStepIndex(-1);
        methods.reset(FLEX_DRAWER_FILTER_DEFAULT_VALUES);
        setFilters(DEFAULT_EMPLOYEE_FLEX_FILTER);
      } else {
        // pick the nearest neighbor (prefer previous)
        const fallback = Math.min(idx, next.length - 1);
        setCurrentStepIndex(fallback);
        applyStepFilters(next[fallback]);
      }
    } else if (idx < currentStepIndex) {
      // deleted a step before current selection, shift left
      setCurrentStepIndex(currentStepIndex - 1);
    }

    persistSteps(next);
    return next;
  });
};

export const makeId = () =>
  globalThis.crypto?.randomUUID?.() ?? `draft-${Date.now()}-${Math.random()}`;

export const extractCheckedFromValues = (values: Record<string, any>) => {
  const out: Record<string, any> = {};
  for (const [key, value] of Object.entries(values)) {
    if (key === 'stepName') continue;
    if (key === 'balanceHours' && !!value && value !== '') {
      const balance = Number(value);
      if (Number.isFinite(balance)) out[key] = balance;
      continue;
    }
    if (value === true || value === 'true') out[key] = true;
  }
  return out;
};

export const onAddStep = ({
  draftStepId,
  setFilterSteps,
  setDraftStepId,
  setEditingStepId,
  setCurrentStepIndex,
  methods,
  setFilters
}) => {
  // prevent multiple drafts
  if (draftStepId != null) return;

  const id = makeId();
  const currentChecked = extractCheckedFromValues(methods.getValues());

  const draft: FilterSteps = {
    id,
    index: 0,
    name: '',
    checkedFilters: currentChecked
  };

  setFilterSteps((prev) => [
    draft,
    ...prev.map((s, i) => ({ ...s, index: i + 1 }))
  ]);

  setDraftStepId(id);
  setEditingStepId(id);
  setCurrentStepIndex(0);

  const formVals = toFormFromStep(draft);
  methods.reset(formVals);
  setFilters(currentChecked as FilterValues);
};
